import { type ChangeEvent, useEffect, useRef, useState } from 'react'

import {
  Divider,
  Field,
  Flex,
  Form,
  PrimaryButton,
  SecondaryButton,
  Space,
  Tag,
  TertiaryButton,
  Text,
  TextLabel,
  TransparentIconButton,
} from '@procurify/ui'
import { FormikProvider, useFormik } from 'formik'
import moment from 'moment'
import * as Yup from 'yup'

import { FormattedShortDate } from '@webapp/core/components/FormattedDate'
import { useProcurifyIntl } from '@webapp/hooks'
import {
  type CreditCardBillFormSchema,
  type ICreditCardBillFormProps,
} from './CreditCardBillForm.types'
import messages from './messages'

// Due-date presets stamp a clearable payment term relative to the invoice date
// (BHV-045). "Due upon receipt" resolves to today; the NET presets add days.
const PAYMENT_TERM_PRESETS: { key: string; days: Nullable<number> }[] = [
  { key: 'dueUponReceipt', days: null },
  { key: 'net30', days: 30 },
  { key: 'net45', days: 45 },
  { key: 'net60', days: 60 },
  { key: 'net90', days: 90 },
]

// Form content ONLY — the FS owns the Add Payment Method + Attach Invoice
// modals (Modal-Free Feature Design).
export const CreditCardBillForm = ({
  creditCardName,
  creditCardProvider,
  invoiceDate,
  paymentMethodOptions,
  approvalChainOptions,
  approverOptions,
  skipApproval,
  isApproverLoading,
  isSubmitting,
  matchedItemsSlot,
  onUploadInvoice,
  onAttachInvoice,
  onAddPaymentMethod,
  onApprovalChainChange,
  onSubmit,
  onCancel,
}: ICreditCardBillFormProps) => {
  const intl = useProcurifyIntl()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)

  const validationSchema = Yup.object({
    paymentMethod: Yup.object()
      .nullable()
      .required(intl.formatMessage(messages.paymentMethodRequiredError)),
    dueDate: Yup.mixed().required(
      intl.formatMessage(messages.dueDateRequiredError)
    ),
    approver: skipApproval
      ? Yup.object().nullable()
      : Yup.object()
          .nullable()
          .required(intl.formatMessage(messages.approverRequiredError)),
  })

  const formik = useFormik<CreditCardBillFormSchema>({
    initialValues: {
      paymentMethod: null,
      dueDate: moment(),
      paymentTerms: null,
      notes: '',
      approvalGroup: null,
      approver: null,
      invoiceAttachments: [],
    },
    validationSchema,
    onSubmit: (submittedValues, { setSubmitting }) => {
      onSubmit(submittedValues)
      setSubmitting(false)
    },
  })

  const { values, setFieldValue } = formik
  const selectedChainId = values.approvalGroup?.value ?? null

  // Chain select resets approver + triggers the choices fetch (BHV-046).
  useEffect(() => {
    onApprovalChainChange(selectedChainId)
    setFieldValue('approver', null)
    // Intentionally keyed on the chain id only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChainId])

  // A single available choice auto-selects (BHV-046).
  useEffect(() => {
    if (selectedChainId !== null && approverOptions.length === 1) {
      setFieldValue('approver', approverOptions[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approverOptions, selectedChainId])

  const applyPaymentTermPreset = (key: string, days: Nullable<number>) => {
    const label = intl.formatMessage(messages[key as keyof typeof messages])
    const dueDate =
      days === null ? moment() : moment(invoiceDate).add(days, 'days')
    setFieldValue('dueDate', dueDate)
    setFieldValue('paymentTerms', label)
  }

  const handleFileSelected = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null
    event.target.value = ''
    if (!file) return

    setIsUploading(true)
    try {
      const uploaded = await onUploadInvoice(file)
      if (uploaded) {
        setFieldValue('invoiceAttachments', [
          ...values.invoiceAttachments,
          uploaded,
        ])
      }
    } finally {
      setIsUploading(false)
    }
  }

  const removeInvoice = (uuid: string) => {
    setFieldValue(
      'invoiceAttachments',
      values.invoiceAttachments.filter((invoice) => invoice.uuid !== uuid)
    )
  }

  return (
    <FormikProvider value={formik}>
      <Form.Content noValidate autoComplete='off'>
        <Space size='large'>
          <Flex.Row>
            {/* Header block — left column */}
            <Flex.Item width={1 / 2}>
              <Space>
                <Space direction='horizontal' size='large'>
                  <Space.Item minWidth={200}>
                    <Field label={intl.formatMessage(messages.creditCardLabel)}>
                      <Text autoMargin={false}>{creditCardName}</Text>
                    </Field>
                  </Space.Item>
                  <Space.Item minWidth={200}>
                    <Field
                      label={intl.formatMessage(
                        messages.creditCardProviderLabel
                      )}
                    >
                      <Text autoMargin={false}>{creditCardProvider}</Text>
                    </Field>
                  </Space.Item>
                </Space>

                <Space size='small'>
                  <Space direction='horizontal' justify='end'>
                    <TertiaryButton type='button' onClick={onAddPaymentMethod}>
                      {intl.formatMessage(messages.addMethodButton)}
                    </TertiaryButton>
                  </Space>
                  <Form.Select
                    id='paymentMethod'
                    name='paymentMethod'
                    label={intl.formatMessage(messages.paymentMethodLabel)}
                    placeholder={intl.formatMessage(
                      messages.paymentMethodPlaceholder
                    )}
                    options={paymentMethodOptions}
                    required
                  />
                </Space>

                <Field label={intl.formatMessage(messages.dateLabel)}>
                  <Text autoMargin={false}>
                    <FormattedShortDate date={invoiceDate} />
                  </Text>
                </Field>

                <Space size='small'>
                  <Form.SingleDatePicker
                    id='dueDate'
                    name='dueDate'
                    label={intl.formatMessage(messages.dueDateLabel)}
                    required
                  />
                  <Space direction='horizontal' size='small' align='center'>
                    {PAYMENT_TERM_PRESETS.map((preset) => (
                      <TertiaryButton
                        key={preset.key}
                        type='button'
                        onClick={() =>
                          applyPaymentTermPreset(preset.key, preset.days)
                        }
                      >
                        {intl.formatMessage(
                          messages[preset.key as keyof typeof messages]
                        )}
                      </TertiaryButton>
                    ))}
                  </Space>
                  {values.paymentTerms && (
                    <Space direction='horizontal' size='small' align='center'>
                      <TextLabel>
                        {intl.formatMessage(messages.paymentTermsLabel)}
                      </TextLabel>
                      <Tag>{values.paymentTerms}</Tag>
                      <TertiaryButton
                        type='button'
                        onClick={() => setFieldValue('paymentTerms', null)}
                      >
                        {intl.formatMessage(messages.clearButton)}
                      </TertiaryButton>
                    </Space>
                  )}
                </Space>
              </Space>
            </Flex.Item>

            {/* Invoice attachment — right column */}
            <Flex.Item width={1 / 2}>
              <Space size='small'>
                <Text bold autoMargin={false}>
                  {intl.formatMessage(messages.invoiceAttachmentTitle)}
                </Text>
                <input
                  ref={fileInputRef}
                  type='file'
                  hidden
                  aria-hidden
                  onChange={handleFileSelected}
                  data-testid='credit-card-bill-form-file-input'
                />
                <Space direction='horizontal' size='small' align='center'>
                  <SecondaryButton
                    type='button'
                    icon='upload'
                    isLoading={isUploading}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {intl.formatMessage(messages.uploadButton)}
                  </SecondaryButton>
                  <Text color='textSecondary' autoMargin={false}>
                    {intl.formatMessage(messages.maxFileSizeText)}
                  </Text>
                </Space>
                {values.invoiceAttachments.map((invoice) => (
                  <Space
                    key={invoice.uuid}
                    direction='horizontal'
                    justify='space-between'
                    align='center'
                  >
                    <Text autoMargin={false}>{invoice.name}</Text>
                    <Space direction='horizontal' size='small' align='center'>
                      <TertiaryButton
                        type='button'
                        onClick={() => onAttachInvoice(invoice.uuid)}
                      >
                        {intl.formatMessage(messages.attachToItemsButton)}
                      </TertiaryButton>
                      <TransparentIconButton
                        icon='delete'
                        type='button'
                        aria-label={intl.formatMessage(
                          messages.removeInvoiceAriaLabel,
                          { name: invoice.name }
                        )}
                        onClick={() => removeInvoice(invoice.uuid)}
                      />
                    </Space>
                  </Space>
                ))}
              </Space>
            </Flex.Item>
          </Flex.Row>

          <Space size='small'>
            <Text bold autoMargin={false}>
              {intl.formatMessage(messages.matchedItemsTitle)}
            </Text>
            {matchedItemsSlot}
          </Space>

          <Flex.Row>
            <Flex.Item width={1 / 2}>
              <Form.TextField
                id='notes'
                name='notes'
                label={intl.formatMessage(messages.notesLabel)}
                multiline
                rows={4}
              />
            </Flex.Item>
            <Flex.Item width={1 / 2}>
              {!skipApproval && (
                <Flex.Row>
                  <Flex.Item width={1 / 2}>
                    <Form.Select
                      id='approvalGroup'
                      name='approvalGroup'
                      label={intl.formatMessage(messages.approvalGroupLabel)}
                      placeholder={intl.formatMessage(
                        messages.approvalGroupPlaceholder
                      )}
                      options={approvalChainOptions}
                    />
                  </Flex.Item>
                  <Flex.Item width={1 / 2}>
                    <Form.Select
                      id='approver'
                      name='approver'
                      label={intl.formatMessage(messages.approverLabel)}
                      placeholder={intl.formatMessage(
                        messages.approverPlaceholder
                      )}
                      options={approverOptions}
                      isDisabled={!values.approvalGroup}
                      isLoading={isApproverLoading}
                      required
                    />
                  </Flex.Item>
                </Flex.Row>
              )}
            </Flex.Item>
          </Flex.Row>

          <Divider />
          <Space direction='horizontal' justify='end' align='center'>
            <TertiaryButton
              type='button'
              onClick={onCancel}
              disabled={isSubmitting}
            >
              {intl.formatMessage(messages.cancelButton)}
            </TertiaryButton>
            <PrimaryButton type='submit' isLoading={isSubmitting}>
              {intl.formatMessage(messages.submitButton)}
            </PrimaryButton>
          </Space>
        </Space>
      </Form.Content>
    </FormikProvider>
  )
}

CreditCardBillForm.displayName = 'CreditCardBillForm'
